/**
 * mSTEM AI control panel modal — "Bảng điều khiển AI" (Phase 7 / AI-01, DR-04).
 *
 * Presentational 2-column panel (props-driven, no Redux, no VM). All state lives in
 * the container (containers/ai-panel-modal.jsx):
 *   - LEFT (~55%): "Camera" heading + a 16:9 dark (#1A1F36) box. When props.cameraOn
 *     a live <video> (wired via props.videoRefCallback) fills the box; otherwise a
 *     centered webcam glyph + caption (cameraPlaceholder, or cameraDenied when
 *     props.cameraError). Below the box a single solid-navy primary button toggles
 *     "Bật camera" / "Tắt camera" → props.onEnableCamera. (AI-06 feasibility nod —
 *     getUserMedia live preview; no inference overlay.)
 *   - RIGHT (~45%): "Chức năng AI" heading + 4 control cards (Khuôn mặt / Cơ thể /
 *     Hình ảnh / OCR), each with navy-OUTLINE action buttons mirroring the AI blocks
 *     → props.onControlClick(controlId). A shared result area at the bottom shows
 *     "Đang phân tích…" (props.analyzing), then the mock result (props.result:
 *     {kind:'table'|'badges'|'text'}), else the resultEmpty hint.
 *
 * All user-facing copy via gen.ai.panel.* (VN default, extracted by npm run i18n:src).
 * React 16 — no inline JSX arrow handlers (react/jsx-no-bind): per-button click is
 * bound in the ControlButton inner class; per-card click routes through onControlClick.
 */
import PropTypes from 'prop-types';
import React from 'react';
import bindAll from 'lodash.bindall';
import {defineMessages, FormattedMessage, injectIntl, intlShape} from 'react-intl';

import Modal from '../../containers/modal.jsx';
import MstemTable from '../mstem-table/mstem-table.jsx';
import MstemBadge from '../mstem-badge/mstem-badge.jsx';

import styles from './ai-panel-modal.css';

const messages = defineMessages({
    title: {
        defaultMessage: 'Bảng điều khiển AI',
        description: 'AI panel modal title (AI-01)',
        id: 'gen.ai.panel.title'
    },
    cameraHeading: {
        defaultMessage: 'Camera',
        description: 'AI panel left-column heading',
        id: 'gen.ai.panel.cameraHeading'
    },
    cameraPlaceholder: {
        defaultMessage: 'Camera mô phỏng — nhấn "Bật camera" để xem hình ảnh thật.',
        description: 'AI panel camera-off placeholder caption',
        id: 'gen.ai.panel.cameraPlaceholder'
    },
    enableCamera: {
        defaultMessage: 'Bật camera',
        description: 'AI panel primary CTA — enable camera',
        id: 'gen.ai.panel.enableCamera'
    },
    disableCamera: {
        defaultMessage: 'Tắt camera',
        description: 'AI panel camera-on toggle — disable camera',
        id: 'gen.ai.panel.disableCamera'
    },
    cameraDenied: {
        defaultMessage: 'Không truy cập được camera. Hãy cho phép quyền camera trong trình duyệt, hoặc tiếp tục với dữ liệu mô phỏng.', // eslint-disable-line max-len
        description: 'AI panel camera-denied fallback caption',
        id: 'gen.ai.panel.cameraDenied'
    },
    controlsHeading: {
        defaultMessage: 'Chức năng AI',
        description: 'AI panel right-column heading',
        id: 'gen.ai.panel.controlsHeading'
    },
    cardFace: {
        defaultMessage: 'Khuôn mặt',
        description: 'AI panel control card head — Face',
        id: 'gen.ai.panel.cardFace'
    },
    cardBody: {
        defaultMessage: 'Cơ thể',
        description: 'AI panel control card head — Body',
        id: 'gen.ai.panel.cardBody'
    },
    cardImage: {
        defaultMessage: 'Hình ảnh',
        description: 'AI panel control card head — Image',
        id: 'gen.ai.panel.cardImage'
    },
    cardOcr: {
        defaultMessage: 'OCR (chữ tiếng Anh)',
        description: 'AI panel control card head — OCR',
        id: 'gen.ai.panel.cardOcr'
    },
    analyzing: {
        defaultMessage: 'Đang phân tích…',
        description: 'AI panel result-area loading state',
        id: 'gen.ai.panel.analyzing'
    },
    resultEmpty: {
        defaultMessage: 'Nhấn một nút bên trên để xem kết quả mô phỏng.',
        description: 'AI panel result-area empty hint',
        id: 'gen.ai.panel.resultEmpty'
    },
    resultHead: {
        defaultMessage: 'Kết quả',
        description: 'AI panel result-area heading',
        id: 'gen.ai.panel.resultHead'
    },
    close: {
        defaultMessage: 'Đóng',
        description: 'AI panel close button',
        id: 'gen.ai.panel.close'
    },
    // ── Right-column action buttons (mirror the AI blocks) ──────────────
    btnFaceCount: {
        defaultMessage: 'Đếm khuôn mặt',
        description: 'AI panel Face card button — count faces',
        id: 'gen.ai.panel.btn.face.count'
    },
    btnFaceIdentify: {
        defaultMessage: 'Nhận dạng khuôn mặt',
        description: 'AI panel Face card button — identify faces',
        id: 'gen.ai.panel.btn.face.identify'
    },
    btnFaceAnalyzeSave: {
        defaultMessage: 'Phân tích & lưu',
        description: 'AI panel Face card button — analyze & save',
        id: 'gen.ai.panel.btn.face.analyzeSave'
    },
    btnBodyObjectCount: {
        defaultMessage: 'Đếm đối tượng',
        description: 'AI panel Body card button — count objects',
        id: 'gen.ai.panel.btn.body.objectCount'
    },
    btnBodyPartXY: {
        defaultMessage: 'Vị trí bộ phận',
        description: 'AI panel Body card button — body part positions',
        id: 'gen.ai.panel.btn.body.partXY'
    },
    btnBodyAnalyzeHand: {
        defaultMessage: 'Phân tích bàn tay',
        description: 'AI panel Body card button — analyze hand',
        id: 'gen.ai.panel.btn.body.analyzeHand'
    },
    btnImageCaptureIdentify: {
        defaultMessage: 'Chụp & nhận dạng',
        description: 'AI panel Image card button — capture & identify',
        id: 'gen.ai.panel.btn.image.captureIdentify'
    },
    btnImageObjectCount: {
        defaultMessage: 'Đếm đối tượng',
        description: 'AI panel Image card button — count objects',
        id: 'gen.ai.panel.btn.image.objectCount'
    },
    btnImageBrand: {
        defaultMessage: 'Nhận dạng thương hiệu',
        description: 'AI panel Image card button — identify brand',
        id: 'gen.ai.panel.btn.image.brand'
    },
    btnOcrFromCamera: {
        defaultMessage: 'Đọc chữ từ camera',
        description: 'AI panel OCR card button — read text from camera',
        id: 'gen.ai.panel.btn.ocr.fromCamera'
    }
});

// Card definitions — each button maps an i18n message to a controlId the
// ai-panel-results helper understands. Static, declared once.
const CARDS = [
    {
        head: messages.cardFace,
        buttons: [
            {controlId: 'face.count', label: messages.btnFaceCount},
            {controlId: 'face.identify', label: messages.btnFaceIdentify},
            {controlId: 'face.analyzeSave', label: messages.btnFaceAnalyzeSave}
        ]
    },
    {
        head: messages.cardBody,
        buttons: [
            {controlId: 'body.objectCount', label: messages.btnBodyObjectCount},
            {controlId: 'body.partXY', label: messages.btnBodyPartXY},
            {controlId: 'body.analyzeHand', label: messages.btnBodyAnalyzeHand}
        ]
    },
    {
        head: messages.cardImage,
        buttons: [
            {controlId: 'image.captureIdentify', label: messages.btnImageCaptureIdentify},
            {controlId: 'image.objectCount', label: messages.btnImageObjectCount},
            {controlId: 'image.brand', label: messages.btnImageBrand}
        ]
    },
    {
        head: messages.cardOcr,
        buttons: [
            {controlId: 'ocr.fromCamera', label: messages.btnOcrFromCamera}
        ]
    }
];

/**
 * Inner control button — binds its own click to the parent's onClick(controlId)
 * so we avoid inline JSX arrows (react/jsx-no-bind) in the card map.
 */
class ControlButton extends React.Component {
    constructor (props) {
        super(props);
        bindAll(this, ['handleClick']);
    }
    handleClick () {
        this.props.onClick(this.props.controlId);
    }
    render () {
        return (
            <button
                type="button"
                className={styles.controlButton}
                onClick={this.handleClick}
            >
                <FormattedMessage {...this.props.label} />
            </button>
        );
    }
}

ControlButton.propTypes = {
    controlId: PropTypes.string.isRequired,
    label: PropTypes.object.isRequired, // eslint-disable-line react/forbid-prop-types
    onClick: PropTypes.func.isRequired
};

// Inline webcam glyph — white on the dark camera box (no external asset).
const webcamGlyph = (
    <svg
        className={styles.webcamGlyph}
        width="64"
        height="64"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.6"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <circle
            cx="12"
            cy="10"
            r="6"
        />
        <circle
            cx="12"
            cy="10"
            r="2.2"
        />
        <path d="M7 19h10" />
        <path d="M12 16v3" />
    </svg>
);

const renderResult = (result, intl) => {
    if (result.kind === 'table') {
        return (
            <MstemTable
                columns={result.payload.columns}
                data={result.payload.rows}
                emptyMessage={intl.formatMessage(messages.resultEmpty)}
            />
        );
    }
    if (result.kind === 'badges') {
        return (
            <div className={styles.badgeRow}>
                {result.payload.map((label, idx) => (
                    <MstemBadge
                        key={idx}
                        variant="info"
                    >
                        {label}
                    </MstemBadge>
                ))}
            </div>
        );
    }
    // kind 'text'
    return (
        <p className={styles.resultText}>{String(result.payload)}</p>
    );
};

const AiPanelModalComponent = props => {
    const {intl, onRequestClose, onEnableCamera, onControlClick,
        cameraOn, cameraError, analyzing, result, videoRefCallback} = props;
    return (
        <Modal
            className={styles.modalContent}
            contentLabel={intl.formatMessage(messages.title)}
            headerClassName={styles.header}
            id="aiPanelModal"
            onRequestClose={onRequestClose}
        >
            <div className={styles.body}>
                <div className={styles.columns}>
                    {/* LEFT — camera column */}
                    <div className={styles.cameraCol}>
                        <p className={styles.sectionHead}>
                            <FormattedMessage {...messages.cameraHeading} />
                        </p>
                        <div className={styles.cameraBox}>
                            <video
                                ref={videoRefCallback}
                                className={cameraOn ? styles.cameraVideo : styles.cameraVideoHidden}
                                autoPlay
                                muted
                                playsInline
                            />
                            {cameraOn ? null : (
                                <div className={styles.cameraOverlay}>
                                    {webcamGlyph}
                                    <p className={styles.cameraCaption}>
                                        {cameraError ? (
                                            <FormattedMessage {...messages.cameraDenied} />
                                        ) : (
                                            <FormattedMessage {...messages.cameraPlaceholder} />
                                        )}
                                    </p>
                                </div>
                            )}
                        </div>
                        <button
                            type="button"
                            className={styles.primaryButton}
                            onClick={onEnableCamera}
                        >
                            {cameraOn ? (
                                <FormattedMessage {...messages.disableCamera} />
                            ) : (
                                <FormattedMessage {...messages.enableCamera} />
                            )}
                        </button>
                    </div>

                    {/* RIGHT — controls column */}
                    <div className={styles.controlsCol}>
                        <p className={styles.sectionHead}>
                            <FormattedMessage {...messages.controlsHeading} />
                        </p>
                        {CARDS.map((card, idx) => (
                            <div
                                key={idx}
                                className={styles.card}
                            >
                                <div className={styles.cardHead}>
                                    <FormattedMessage {...card.head} />
                                </div>
                                <div className={styles.cardButtons}>
                                    {card.buttons.map(btn => (
                                        <ControlButton
                                            key={btn.controlId}
                                            controlId={btn.controlId}
                                            label={btn.label}
                                            onClick={onControlClick}
                                        />
                                    ))}
                                </div>
                            </div>
                        ))}

                        {/* Shared result area */}
                        <div className={styles.resultArea}>
                            <p className={styles.resultHead}>
                                <FormattedMessage {...messages.resultHead} />
                            </p>
                            {analyzing ? (
                                <p className={styles.analyzing}>
                                    <FormattedMessage {...messages.analyzing} />
                                </p>
                            ) : (result ? (
                                renderResult(result, intl)
                            ) : (
                                <p className={styles.resultEmpty}>
                                    <FormattedMessage {...messages.resultEmpty} />
                                </p>
                            ))}
                        </div>
                    </div>
                </div>

                <div className={styles.footer}>
                    <button
                        type="button"
                        className={styles.closeButton}
                        onClick={onRequestClose}
                    >
                        <FormattedMessage {...messages.close} />
                    </button>
                </div>
            </div>
        </Modal>
    );
};

AiPanelModalComponent.propTypes = {
    analyzing: PropTypes.bool,
    cameraError: PropTypes.bool,
    cameraOn: PropTypes.bool,
    intl: intlShape.isRequired,
    onControlClick: PropTypes.func,
    onEnableCamera: PropTypes.func,
    onRequestClose: PropTypes.func,
    result: PropTypes.shape({
        kind: PropTypes.oneOf(['text', 'badges', 'table']),
        payload: PropTypes.any // eslint-disable-line react/forbid-prop-types
    }),
    videoRefCallback: PropTypes.func
};

AiPanelModalComponent.defaultProps = {
    analyzing: false,
    cameraError: false,
    cameraOn: false,
    onControlClick: () => {},
    onEnableCamera: () => {},
    onRequestClose: () => {},
    result: null,
    videoRefCallback: () => {}
};

export default injectIntl(AiPanelModalComponent);
